import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Button, Card, Spin, Col, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import DataTable from "../DataTable/DataTable";
import { fetchEnrollmentPolicyType, getAllCorporateEndorsementCalculations } from "../../Action/Actions";
import { getUserData } from "../login/LoginSlice";
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice";
import "./cd.css";

const RESTRICTED_POLICY_TYPE_IDS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

const EndorsmentList = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUserData);
  const selectedYearId = useSelector(selectSelectedYearId);
  const yearList = useSelector(selectFinancialYears);

  const [loading, setLoading] = useState(false);
  const [policyData, setPolicyData] = useState({});
  const [activePolicyType, setActivePolicyType] = useState(null);
  const [selectedPolicyNumber, setSelectedPolicyNumber] = useState(null);
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [endorsementData, setEndorsementData] = useState([]);

  const selectedYear = useMemo(() =>
    yearList.find(year => year.id === selectedYearId)?.financial_year || "",
    [yearList, selectedYearId]
  );

  const loadPolicyTypes = useCallback(async () => {
    if (!user?.corporate_id || !selectedYearId) {
      setPolicyData({});
      setActivePolicyType(null);
      setSelectedPolicyNumber(null);
      setSelectedPolicyId(null);
      setEndorsementData([]);
      return;
    }
    try {
      const res = await dispatch(fetchEnrollmentPolicyType(user.corporate_id));
      const policy_type_list = res?.policy_type_list || {};
      
      // NEW: Filter out restricted policy types
      const filtered_policy_type_list = {};
      for (const type in policy_type_list) {
        const typeName = String(type).toLowerCase();
        if (typeName.includes('marine') || typeName.includes('fire')) {
          continue;
        }

        const policies = policy_type_list[type] || [];
        const validPolicies = policies.filter(p => {
          const details = Array.isArray(p.policy_details) ? p.policy_details[0] : p.policy_details;
          const typeId = details?.policy_type_id || p.policy_type_id || p.policy_details?.policy_type_id;
          const isRestrictedId = RESTRICTED_POLICY_TYPE_IDS.includes(Number(typeId));
          return !isRestrictedId;
        });
        if (validPolicies.length > 0) {
          filtered_policy_type_list[type] = validPolicies;
        }
      }

      setPolicyData(filtered_policy_type_list);
      
      const firstType = Object.keys(filtered_policy_type_list || {})[0] || null;
      setActivePolicyType(firstType);
      
      // Auto-select first policy (use policy_number if available, otherwise use policy_id)
      const firstPolicy = firstType ? filtered_policy_type_list[firstType]?.[0] : null;
      
      if (firstPolicy) {
        setSelectedPolicyNumber(firstPolicy.policy_number || null);
        setSelectedPolicyId(firstPolicy.policy_id || null);
      } else {
        setSelectedPolicyNumber(null);
        setSelectedPolicyId(null);
        setEndorsementData([]);
      }
    } catch (err) {
      console.error("Error loading policy types:", err);
      setPolicyData({});
      setActivePolicyType(null);
      setSelectedPolicyNumber(null);
      setSelectedPolicyId(null);
      setEndorsementData([]);
    }
  }, [dispatch, user?.corporate_id, selectedYearId]);

  useEffect(() => {
    document.title = "Endorsements";
    loadPolicyTypes();
  }, [loadPolicyTypes]);

  const loadEndorsementData = useCallback(async () => {
    if (!user?.corporate_id) {
      setEndorsementData([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await dispatch(getAllCorporateEndorsementCalculations(user.corporate_id));
      const endorsements = response?.data?.data || [];
      setEndorsementData(endorsements);
    } catch (error) {
      console.error("Error loading endorsement data:", error);
      setEndorsementData([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, user?.corporate_id]);

  useEffect(() => {
    if (selectedPolicyNumber || selectedPolicyId) {
      loadEndorsementData();
    } else {
      setEndorsementData([]);
    }
  }, [selectedPolicyNumber, selectedPolicyId, loadEndorsementData]);

  const columns = [
    { title: "SI NO", dataIndex: "si_no", render: (_, __, index) => index + 1 },
    { title: "POLICY NUMBER", dataIndex: "policy_number", render: (_, record) => record.corporate_name || "N/A" },
    { title: "ENDORSEMENT TITLE", dataIndex: "endorsement_title" },
    { title: "ENDORSEMENT NUMBER", dataIndex: "endorsement_number" },
    { title: "TRANSACTION STATEMENT", dataIndex: "transaction_statements" },
    { title: "ADDITION PREMIUM", dataIndex: "total_addition_premium", render: (value) => value ? `₹${value.toFixed(2)}` : "₹0.00" },
    { title: "DELETION PREMIUM", dataIndex: "total_deletion_premium", render: (value) => value ? `₹${value.toFixed(2)}` : "₹0.00" },
    { title: "NET PREMIUM", dataIndex: "net_premium", render: (value) => value ? `₹${value.toFixed(2)}` : "₹0.00" },
    { title: "CREATED AT", dataIndex: "created_at", render: (date) => date ? new Date(date).toLocaleDateString('en-IN') : "N/A" },
  ];

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-content mb-3">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>
            <span className="header-year-spacing">
              (Financial Year: {selectedYear})
            </span>
          </Row>

          <Row className="policy-row-spacing policy-tabs-container" gutter={8}>
            {Object.keys(policyData || {}).map((type) => (
              <Col key={type}>
                <Button
                  className={`policy-tab ${type === activePolicyType ? "active" : ""}`}
                  onClick={() => {
                    setActivePolicyType(type);
                    const firstPolicy = policyData[type]?.[0];
                    setSelectedPolicyNumber(firstPolicy?.policy_number || null);
                    setSelectedPolicyId(firstPolicy?.policy_id || null);
                  }}
                >
                  {type === 'GMC' ? <TeamOutlined style={{ color: '#0072bb' }} /> : type === 'GPA' ? <UserOutlined style={{ color: '#0072bb' }} /> : null}
                  <span>{type}</span>
                </Button>
              </Col>
            ))}
          </Row>

          <Row className="policy-row-spacing" gutter={8}>
            {(() => {
              const list = policyData?.[activePolicyType] || [];
              return list.map((p, idx) => (
                <Col key={p?.policy_id ?? `policy-${idx}`}>
                  <Button
                    className={`${p?.policy_id === selectedPolicyId ? 'policy-number active' : 'policy-number'} ${!p.policy_number ? 'policy-not-issued' : ''}`}
                    onClick={() => {
                      setSelectedPolicyNumber(p.policy_number);
                      setSelectedPolicyId(p.policy_id);
                    }}
                  >
                    {p.policy_number ? (
                      p.policy_number
                    ) : (
                      <span
                        style={{
                          backgroundColor: "#FFFF00",
                          color: "#000000E0",
                          padding: "2px 8px",
                          fontSize: "12px",
                          fontFamily: '"Plus Jakarta Sans", sans-serif',
                          borderRadius: "4px",
                          display: "inline-block",
                        }}
                      >
                        Policy Number not issued
                      </span>
                    )}
                  </Button>
                </Col>
              ));
            })()}

            {Object.keys(policyData || {}).length === 0 && (
              <Col span={24}>
                <Card>
                  <p>No policy data available for the selected financial year.</p>
                </Card>
              </Col>
            )}
          </Row>
        </div>

        <Card title="Endorsements">
          <DataTable
            columns={columns}
            dataSource={endorsementData}
            loading={loading}
            bordered
          />
        </Card>
      </div>
    </Spin>
  );
};

export default EndorsmentList;